import { useRef, type ChangeEvent } from "react"

import { validateRegistrationFormValue } from "@/lib/registration-form-utils"
import { selectIsXS } from "@/lib/storage/app-slice"
import {
  selectAuthFlowEmail,
  selectAuthFlowEmailLocked,
  selectAuthFlowLoading,
  selectAuthFlowPassword,
  selectAuthFlowPasswordErrors,
  selectAuthFlowPasswordTouched,
  setAuthFlowCfToken,
  setAuthFlowEmail,
  setAuthFlowError,
  setAuthFlowPassword,
  setAuthFlowPasswordErrors,
  setAuthFlowPasswordTouched,
} from "@/lib/storage/auth-slice"
import { showEmailStep } from "@/lib/storage/auth-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { shouldTurnstile, turnstileErrorAction } from "@/lib/turnstile"
import { isTouch } from "@/lib/utils/dom"
import { Turnstile } from "@/components/turnstile"
import { FormControl, FormError, FormField } from "@/components/ui/form"
import { PasswordInput } from "@/components/ui/password-input"
import { TextInput } from "@/components/ui/text-input"

export function RegistrationForm() {
  const dispatch = useAppDispatch()
  const email = useAppSelector(selectAuthFlowEmail)
  const password = useAppSelector(selectAuthFlowPassword)
  const loading = useAppSelector(selectAuthFlowLoading)
  const emailLocked = useAppSelector(selectAuthFlowEmailLocked)
  const passwordTouched = useAppSelector(selectAuthFlowPasswordTouched)
  const passwordErrors = useAppSelector(selectAuthFlowPasswordErrors)
  const isXS = useAppSelector(selectIsXS)
  const doAutoFocus = !(isXS && isTouch())
  const passwordRef = useRef<HTMLInputElement | null>(null)

  const isEmailReadonly = !!email
  const showEmailChange = isEmailReadonly && !emailLocked

  const handleTurnstileRetry = () => {
    dispatch(setAuthFlowCfToken(""))
  }

  const handleTurnstileError = (errorCode: string) => {
    if (turnstileErrorAction(errorCode) !== "terminal") {
      handleTurnstileRetry()
      return
    }

    handleTurnstileFailure()
  }

  const handleTurnstileFailure = () => {
    dispatch(setAuthFlowCfToken(""))
    dispatch(setAuthFlowError("Please complete the verification"))
  }

  // Update password errors when password changes
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value
    dispatch(setAuthFlowPassword(newPassword))

    // Validate password if it's been touched
    if (passwordTouched && newPassword.length > 0) {
      const [_, __, errors] = validateRegistrationFormValue({
        email,
        password: newPassword,
        cf_token: "",
        cf_token_required: shouldTurnstile("register"),
        organization_email_opt_in: false,
      })
      dispatch(setAuthFlowPasswordErrors(errors?.password?._errors || []))
    }
  }

  return (
    <>
      <FormField
        name="email"
        onClick={
          showEmailChange ? () => void dispatch(showEmailStep()) : undefined
        }
      >
        <FormControl
          render={
            <TextInput
              type="email"
              autoComplete="username"
              placeholder="Email"
              size="lg"
              required
              value={email}
              disabled={loading}
              readOnly={isEmailReadonly}
              onChange={(e) => dispatch(setAuthFlowEmail(e.target.value))}
              autoFocus={!email && doAutoFocus}
              className={showEmailChange ? "pr-20" : undefined}
            />
          }
        />
        {showEmailChange && (
          <button
            type="button"
            className="absolute top-0 right-1 flex h-(--input-lg-size) items-center px-2 text-2xs font-medium hover:underline"
          >
            Change
          </button>
        )}
      </FormField>
      <FormField
        name="password"
        invalid={passwordTouched && passwordErrors.length > 0}
      >
        <PasswordInput
          control={FormControl}
          autoComplete="new-password"
          placeholder="Password"
          size="lg"
          required
          disabled={loading}
          value={password}
          onChange={handlePasswordChange}
          onBlur={() => {
            if (password.length) {
              dispatch(setAuthFlowPasswordTouched(true))
              // Validate on blur
              const [_, __, errors] = validateRegistrationFormValue({
                email,
                password,
                cf_token: "",
                cf_token_required: shouldTurnstile("register"),
                organization_email_opt_in: false,
              })
              dispatch(
                setAuthFlowPasswordErrors(errors?.password?._errors || [])
              )
            }
          }}
          autoFocus={!!email && doAutoFocus}
          ref={passwordRef}
        />
        {passwordTouched && passwordErrors.length > 0 && (
          <>
            {passwordErrors.map((error, i) => (
              <FormError key={`password_errors_${i}`}>{error}</FormError>
            ))}
          </>
        )}
      </FormField>
      {shouldTurnstile("register") && (
        <div className="flex justify-center">
          <Turnstile
            id="registration-turnstile"
            onSuccess={(token) => dispatch(setAuthFlowCfToken(token))}
            onError={handleTurnstileError}
            onExpire={handleTurnstileRetry}
            onUnsupported={handleTurnstileFailure}
            onScriptLoadError={handleTurnstileFailure}
          />
        </div>
      )}
    </>
  )
}
